import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { Button, Input, Modal, Switch, Table, Tabs, Tooltip, Typography } from 'antd';
import { SearchOutlined, SettingOutlined, VerticalAlignMiddleOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { FilterState, LocationType } from '../../../types';
import { LocationTypeSummary } from './types';
import type { PopupTableRow } from '../utils';
import {
  formatCurrency,
  formatExactCurrency,
  formatExactNumber,
  formatNumber,
  handleEntityLinkClick,
  maskEmail,
  maskPhone,
} from '../utils';
import type { TableColumnKey } from '../types';

interface ColumnSettingsModalProps {
  isVisible: boolean;
  onCancel: () => void;
  columns: Record<string, boolean>;
  columnOrder: Record<string, number>;
  onToggle: (key: string) => void;
  onReset: () => void;
  onReorder: (updatedOrder: Record<string, number>) => void;
}

interface StandardMapDrawerProps {
  rows: PopupTableRow[];
  search: string;
  onSearchChange: (value: string) => void;
  columnVisibility: Record<TableColumnKey, boolean>;
  onColumnVisibilityChange: (visibility: Record<TableColumnKey, boolean>) => void;
  onEditLocation: (row: PopupTableRow) => void;
  activeTab?: PopupTableRow['type'];
  onTabChange?: (type: PopupTableRow['type']) => void;
  onClientSelect?: (row: PopupTableRow) => void;
  pageSize?: number;
  allowedTabs?: PopupTableRow['type'][];
}

type SortState = {
  field?: TableColumnKey;
  order?: 'ascend' | 'descend';
};

type ColumnConfig = {
  key: TableColumnKey;
  label: string;
  sortable: boolean;
};

const LOCKED_COLUMNS: TableColumnKey[] = ['name'];

const ColumnSettingsModal = React.memo(({
  isVisible,
  onCancel,
  columns,
  columnOrder,
  onToggle,
  onReset,
  onReorder
}: ColumnSettingsModalProps) => {
  const requiredColumns = ['type', 'name'];
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  // Sort columns by their order
  const sortedColumns = Object.entries(columns)
    .filter(([key]) => key !== 'action' && key !== 'type') // Filter out the action and type columns
    .sort(([a], [b]) => {
      const orderA = columnOrder[a] || 999;
      const orderB = columnOrder[b] || 999;
      return orderA - orderB;
    });

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    // Allow dragging even for required columns
    setDraggedItem(key);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    e.preventDefault();
    setDragOverItem(key);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (draggedItem && dragOverItem && draggedItem !== dragOverItem) {
      const newOrder = { ...columnOrder };
      
      // Find the indices in the sorted array
      const allColumns = Object.entries(newOrder).sort(([, a], [, b]) => a - b);
      const draggedIndex = allColumns.findIndex(([key]) => key === draggedItem);
      const dropIndex = allColumns.findIndex(([key]) => key === dragOverItem);

      if (draggedIndex === -1 || dropIndex === -1) {
        console.error('Could not find indices for drag and drop items', { 
          draggedItem, dragOverItem, allColumns 
        });
        return;
      }

      // Create a new array of column keys in the desired order
      const reorderedColumns = [...allColumns];
      const [draggedColumn] = reorderedColumns.splice(draggedIndex, 1);
      reorderedColumns.splice(dropIndex, 0, draggedColumn);

      // Update the order values based on the new positions, starting from 1
      const updatedOrder = reorderedColumns.reduce((acc, [key], index) => {
        acc[key] = index + 1; // Start from 1 instead of 0
        return acc;
      }, {} as { [key: string]: number });

      onReorder(updatedOrder);
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  return (
    <Modal
      title="Column Settings"
      open={isVisible}
      onCancel={onCancel}
      footer={[
        <Button key="reset" onClick={onReset}>
          Reset to Default
        </Button>,
        <Button key="close" type="primary" onClick={onCancel}>
          Close
        </Button>
      ]}
      bodyStyle={{ 
        maxHeight: '60vh',
        overflow: 'hidden',
        padding: 0 
      }}
      width={600}
    > 
      <div 
        className="pe-2 py-4 column-settings-modal-content" 
        style={{ 
          maxHeight: 'calc(60vh - 40px)',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <div className="mb-4 text-sm text-gray-600">
          Drag and drop to reorder columns. Toggle visibility with the switches.
        </div>
        <div className="space-y-2">
          {sortedColumns.map(([key, value]) => (
            <div
              key={key}
              className={`column-settings-item flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 ${
                dragOverItem === key ? "drag-over" : ""
              } ${draggedItem === key ? "dragging" : ""}`}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, key)}
              onDragEnter={(e) => handleDragEnter(e, key)}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            >
              <div className="flex items-center gap-3 cursor-move">
                <VerticalAlignMiddleOutlined className="text-primary move-icon" />
                <span className="font-medium capitalize">
                  {columnDisplayNames[key] || key.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={requiredColumns.includes(key) ? true : value}
                  onChange={() => onToggle(key)}
                  size="small"
                  disabled={requiredColumns.includes(key)}
                  className={`column-settings-switch ${value ? 'bg-blue-500' : ''}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
});

const columnDisplayNames: Record<string, string> = {
  type: 'Type',
  name: 'Name',
  address: 'Address',
  city: 'City',
  state: 'State',
  zip: 'Zip',
  country: 'Country',
  staff: 'Sales Rep',
  orders: 'Orders',
  products: 'Products',
  quotation_count: 'Quotations',
  enquiry_count: 'Enquiries',
  target: 'Target',
  target_year: 'Target Year',
  target_achievement: 'Target %',
  total_amount: 'Target Achieved',
  revenue_share: 'Revenue %',
  email: 'Email',
  phone: 'Phone',
  action: 'Actions',
};

const BASE_COLUMN_DEFS: ColumnConfig[] = [
  { key: 'type', label: 'Type', sortable: false },
  { key: 'name', label: 'Name', sortable: true },
  { key: 'address', label: 'Address', sortable: false },
  { key: 'city', label: 'City', sortable: true },
  { key: 'state', label: 'State', sortable: false },
  { key: 'zip', label: 'Zip', sortable: false },
  { key: 'country', label: 'Country', sortable: false },
  { key: 'staff', label: 'Sales Rep', sortable: true },
  { key: 'orders', label: 'Orders', sortable: true },
  { key: 'products', label: 'Products', sortable: true },
  { key: 'quotation_count', label: 'Quotations', sortable: true },
  { key: 'enquiry_count', label: 'Enquiries', sortable: true },
  { key: 'target', label: 'Target', sortable: true },
  { key: 'target_year', label: 'Target Year', sortable: true },
  { key: 'target_achievement', label: 'Target %', sortable: true },
  { key: 'total_amount', label: 'Target Achieved', sortable: true },
  { key: 'revenue_share', label: 'Revenue %', sortable: true },
  { key: 'email', label: 'Email', sortable: false },
  { key: 'phone', label: 'Phone', sortable: false },
  { key: 'action', label: 'Actions', sortable: false },
];

const DEFAULT_VISIBILITY: Record<TableColumnKey, boolean> = {
  type: false,
  name: true,
  address: false,
  city: true,
  state: true,
  zip: false,
  country: true,
  staff: false,
  orders: false,
  products: false,
  quotation_count: false,
  enquiry_count: false,
  target: false,
  target_year: false,
  target_achievement: false,
  total_amount: false,
  revenue_share: false,
  email: false,
  phone: false,
  action: true,
};

const deriveVisibility = (
  rows: PopupTableRow[],
  columnVisibility: Record<TableColumnKey, boolean>,
): { columns: ColumnConfig[]; visibility: Record<TableColumnKey, boolean> } => {
  const presentTypes = new Set(rows.map((row) => row.type));
  const hasProductRows = presentTypes.has('product');
  const isProspectOnly = presentTypes.size === 1 && presentTypes.has('prospect');

  const base = BASE_COLUMN_DEFS.map((col) => ({
    ...col,
    label:
      col.key === 'orders'
        ? hasProductRows
          ? 'Total Quantity'
          : col.label
        : col.key === 'products'
        ? hasProductRows
          ? 'Unique Products'
          : col.label
        : col.key === 'total_amount'
        ? hasProductRows
          ? 'Total Amount'
          : col.label
        : col.label,
  }));

  let columnDefs = hasProductRows
    ? base.filter(
        (col) =>
          col.key !== 'target' &&
          col.key !== 'target_year' &&
          col.key !== 'target_achievement' &&
          col.key !== 'revenue_share',
      )
    : base;

  if (isProspectOnly) {
    columnDefs = columnDefs.filter(
      (col) =>
        col.key !== 'orders' &&
        col.key !== 'products' &&
        col.key !== 'target_achievement' &&
        col.key !== 'total_amount' &&
        col.key !== 'revenue_share',
    );
  }

  const visibility: Record<TableColumnKey, boolean> = { ...DEFAULT_VISIBILITY };

  const ensureClientColumns = () => {
    visibility.address = true;
    visibility.zip = true;
    visibility.staff = true;
    visibility.orders = true;
    visibility.products = true;
    visibility.target = true;
    visibility.target_year = true;
    visibility.target_achievement = true;
    visibility.total_amount = true;
    visibility.revenue_share = true;
    visibility.action = true;
  };

  const ensureProspectColumns = () => {
    visibility.address = true;
    visibility.zip = true;
    visibility.staff = true;
    visibility.orders = false;
    visibility.products = false;
    visibility.quotation_count = true;
    visibility.enquiry_count = true;
    visibility.target = true;
    visibility.target_year = true;
    visibility.target_achievement = false;
    visibility.total_amount = false;
    visibility.revenue_share = false;
    visibility.action = true;
  };

  const ensureVendorColumns = () => {
    visibility.address = true;
    visibility.zip = true;
    visibility.email = true;
    visibility.phone = true;
    visibility.action = true;
  };

  const ensureProductColumns = () => {
    visibility.city = true;
    visibility.state = true;
    visibility.country = true;
    visibility.orders = true;
    visibility.products = true;
    visibility.total_amount = true;
  };

  presentTypes.forEach((type) => {
    if (type === 'client') ensureClientColumns();
    else if (type === 'prospect') ensureProspectColumns();
    else if (type === 'vendor') ensureVendorColumns();
    else if (type === 'product') ensureProductColumns();
  });

  Object.entries(columnVisibility).forEach(([key, value]) => {
    if (value !== undefined) {
      visibility[key as TableColumnKey] = Boolean(value);
    }
  });

  visibility.name = true;

  const normalizedVisibility: Record<TableColumnKey, boolean> = {} as Record<TableColumnKey, boolean>;
  columnDefs.forEach((col) => {
    normalizedVisibility[col.key] = Boolean(visibility[col.key]);
  });

  return { columns: columnDefs, visibility: normalizedVisibility };
};

const matchesSearch = (row: PopupTableRow, query: string) => {
  if (!query) return true;
  const normalized = query.toLowerCase();
  const parts = [
    row.name,
    row.city,
    row.state,
    row.country,
    row.staff,
    row.email,
    row.phone,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
  return parts.some((part) => part.includes(normalized));
};

const parseAmount = (value?: string | number) => {
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return value;
  const trimmed = value.trim();
  if (/m$/i.test(trimmed)) return parseFloat(trimmed) * 1_000_000;
  if (/k$/i.test(trimmed)) return parseFloat(trimmed) * 1_000;
  const numeric = parseFloat(trimmed.replace(/[^0-9.-]+/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const parsePercent = (value?: string | number) => {
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return value;
  const trimmed = value.trim();
  if (!trimmed) return 0;
  if (/%$/.test(trimmed)) {
    const numeric = parseFloat(trimmed.replace('%', ''));
    return Number.isFinite(numeric) ? numeric : 0;
  }
  const numeric = parseFloat(trimmed);
  return Number.isFinite(numeric) ? numeric : 0;
};

const StandardMapDrawerContent: React.FC<StandardMapDrawerProps> = ({
  rows,
  search,
  onSearchChange,
  columnVisibility,
  onColumnVisibilityChange,
  onEditLocation,
  activeTab,
  onTabChange,
  onClientSelect,
  pageSize: controlledPageSize = 10,
  allowedTabs,
}) => {
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: controlledPageSize,
    showSizeChanger: true,
  });
  const [internalActiveTab, setInternalActiveTab] = useState<PopupTableRow['type']>('client');
  const resolvedActiveTab = activeTab ?? internalActiveTab;
  const [sortState, setSortState] = useState<SortState>({});
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [columnOrder, setColumnOrder] = useState<Record<string, number>>({});
  
  const initialVisibilityByTabRef = useRef<Record<PopupTableRow['type'], Record<TableColumnKey, boolean>>>(
    {} as Record<PopupTableRow['type'], Record<TableColumnKey, boolean>>,
  );

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageSize: controlledPageSize,
    }));
  }, [controlledPageSize]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [search, rows, resolvedActiveTab]);

  const tabFilteredRows = useMemo(
    () => rows.filter((row) => row.type === resolvedActiveTab),
    [rows, resolvedActiveTab],
  );

  useEffect(() => {
    const presentTypes = Array.from(new Set(rows.map((row) => row.type)));
    if (presentTypes.length === 1) {
      setInternalActiveTab(presentTypes[0]);
      onTabChange?.(presentTypes[0]);
      return;
    }
    if (!presentTypes.includes(resolvedActiveTab)) {
      setInternalActiveTab('client');
      onTabChange?.('client');
    }
  }, [rows, resolvedActiveTab, onTabChange]);

  const { columns: availableColumns, visibility } = useMemo(
    () => deriveVisibility(tabFilteredRows, columnVisibility),
    [tabFilteredRows, columnVisibility],
  );

  useEffect(() => {
    if (!initialVisibilityByTabRef.current[resolvedActiveTab]) {
      initialVisibilityByTabRef.current[resolvedActiveTab] = visibility;
    }
  }, [resolvedActiveTab, visibility]);

  const filteredRows = useMemo(
    () => tabFilteredRows.filter((row) => matchesSearch(row, search)),
    [tabFilteredRows, search],
  );

  const totalRevenueAmount = useMemo(() => {
    return filteredRows.reduce((sum, row) => {
      if (row.type !== 'client') return sum;
      return sum + parseAmount(row.total_amount);
    }, 0);
  }, [filteredRows]);

  const handleTableChange = (
    nextPagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<PopupTableRow> | SorterResult<PopupTableRow>[],
    _extra: unknown,
  ) => {
    const primarySorter = Array.isArray(sorter) ? sorter[0] : sorter;
    setPagination({
      ...nextPagination,
      showSizeChanger: true,
    });
    setSortState({
      field: (primarySorter?.field as TableColumnKey) || undefined,
      order: primarySorter?.order as SortState['order'],
    });
  };

  const handleColumnToggle = (key: string) => {
    if (LOCKED_COLUMNS.includes(key as TableColumnKey)) {
      return;
    }
    const next: Record<TableColumnKey, boolean> = {
      ...columnVisibility,
      [key]: !columnVisibility[key as TableColumnKey],
    };
    LOCKED_COLUMNS.forEach((locked) => {
      next[locked] = true;
    });
    onColumnVisibilityChange(next);
  };

  const handleResetColumns = () => {
    const defaultOrder: Record<string, number> = {};
    availableColumns.forEach((col, index) => {
      defaultOrder[col.key] = index + 1;
    });
    setColumnOrder(defaultOrder);

    // Reset column visibility to the captured initial state for this tab
    const initialVisibility =
      initialVisibilityByTabRef.current[resolvedActiveTab] ?? visibility;
    onColumnVisibilityChange(initialVisibility);
  };

  const handleReorder = (updatedOrder: Record<string, number>) => {
    setColumnOrder(updatedOrder);
  };

  useEffect(() => {
    // Only initialize columnOrder if it's empty or if new columns are added
    setColumnOrder((prevOrder) => {
      const hasExistingOrder = Object.keys(prevOrder).length > 0;
      
      if (!hasExistingOrder) {
        // First initialization
        const defaultOrder: Record<string, number> = {};
        availableColumns.forEach((col, index) => {
          defaultOrder[col.key] = index + 1;
        });
        return defaultOrder;
      }
      
      // Check if there are new columns that need to be added to the order
      const newOrder = { ...prevOrder };
      let maxOrder = Math.max(...Object.values(prevOrder));
      let hasNewColumns = false;
      
      availableColumns.forEach((col) => {
        if (!newOrder[col.key]) {
          newOrder[col.key] = ++maxOrder;
          hasNewColumns = true;
        }
      });
      
      return hasNewColumns ? newOrder : prevOrder;
    });
  }, [availableColumns]);



  const columns = useMemo<ColumnsType<PopupTableRow>>(() => {
    const sortedAvailableColumns = [...availableColumns].sort((a, b) => {
      const aOrder = columnOrder[a.key] || 0;
      const bOrder = columnOrder[b.key] || 0;
      return aOrder - bOrder;
    });

    const sortOrderFor = (key: TableColumnKey) => (sortState.field === key ? sortState.order : undefined);

    const renderAmount = (value?: string | number) => {
      if (value === undefined || value === null || value === '') return '';
      const numeric = parseAmount(value);
      const display = Number.isFinite(numeric) ? formatCurrency(numeric) : String(value);
      if (!Number.isFinite(numeric) || numeric === 0) {
        return display;
      }
      return (
        <Tooltip title={formatExactCurrency(numeric)}>
          <span>{display}</span>
        </Tooltip>
      );
    };

    const renderPercent = (value?: string | number) => {
      if (value === undefined || value === null || value === '') return '';
      const numeric = parsePercent(value);
      const display =
        typeof value === 'number'
          ? `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`
          : String(value);
      return (
        <Tooltip title={`${numeric.toFixed(2)}%`}>
          <span>{display}</span>
        </Tooltip>
      );
    };

    const getRevenueShare = (row: PopupTableRow) => {
      if (row.type !== 'client' || totalRevenueAmount <= 0) {
        return 0;
      }
      const amount = parseAmount(row.total_amount);
      if (amount <= 0) {
        return 0;
      }
      return (amount / totalRevenueAmount) * 100;
    };

    const toDisplayValue = (input: unknown): ReactNode => {
      if (input === null || input === undefined) {
        return '';
      }
      if (typeof input === 'string' || typeof input === 'number') {
        return input;
      }
      return String(input);
    };

    const numericSorter = (a: PopupTableRow, b: PopupTableRow, key: TableColumnKey) => {
      if (key === 'orders' || key === 'products') {
        const av = Number(a[key] ?? 0);
        const bv = Number(b[key] ?? 0);
        return av - bv;
      }
      if (key === 'quotation_count' || key === 'enquiry_count') {
        const av = Number(a[key] ?? 0);
        const bv = Number(b[key] ?? 0);
        return av - bv;
      }
      if (key === 'target' || key === 'total_amount') {
        return parseAmount(a[key]) - parseAmount(b[key]);
      }
      if (key === 'target_year') {
        const av = Number(a[key] ?? 0);
        const bv = Number(b[key] ?? 0);
        return av - bv;
      }
      if (key === 'target_achievement') {
        return parsePercent(a[key]) - parsePercent(b[key]);
      }
      if (key === 'revenue_share') {
        return getRevenueShare(a) - getRevenueShare(b);
      }
      return 0;
    };

    const sorters: Partial<Record<TableColumnKey, (a: PopupTableRow, b: PopupTableRow) => number>> = {
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      city: (a, b) => (a.city || '').localeCompare(b.city || ''),
      staff: (a, b) => (a.staff || '').localeCompare(b.staff || ''),
      orders: (a, b) => numericSorter(a, b, 'orders'),
      products: (a, b) => numericSorter(a, b, 'products'),
      quotation_count: (a, b) => numericSorter(a, b, 'quotation_count'),
      enquiry_count: (a, b) => numericSorter(a, b, 'enquiry_count'),
      target: (a, b) => numericSorter(a, b, 'target'),
      target_year: (a, b) => numericSorter(a, b, 'target_year'),
      target_achievement: (a, b) => numericSorter(a, b, 'target_achievement'),
      total_amount: (a, b) => numericSorter(a, b, 'total_amount'),
      revenue_share: (a, b) => numericSorter(a, b, 'revenue_share'),
    };

    return sortedAvailableColumns
      .filter((col) => visibility[col.key])
      .map((col) => {
        const alignRight =
          col.key === 'orders' ||
          col.key === 'products' ||
          col.key === 'quotation_count' ||
          col.key === 'enquiry_count' ||
          col.key === 'target' ||
          col.key === 'target_year' ||
          col.key === 'target_achievement' ||
          col.key === 'total_amount' ||
          col.key === 'revenue_share';

        const column: ColumnsType<PopupTableRow>[number] = {
          key: col.key,
          dataIndex: col.key,
          title: col.key === 'action' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Actions
              <Tooltip title="Column Setting">
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={() => setIsSettingsModalVisible(true)}
                size="small"
              />
              </Tooltip>
            </div>
          ) : (
            col.label
          ),
          align: alignRight ? 'right' : 'left',
          sortOrder: col.sortable ? sortOrderFor(col.key) : undefined,
          sorter: col.sortable ? sorters[col.key] || undefined : undefined,
          fixed: col.key === 'type' || col.key === 'name' ? 'left' : col.key === 'action' ? 'right' : undefined,

          render: (
            value: PopupTableRow[keyof PopupTableRow] | undefined,
            record: PopupTableRow,
          ): ReactNode => {
            switch (col.key) {
              case 'type':
                return <span style={{ textTransform: 'capitalize' }}>{record.type}</span>;
              case 'name':
                if (record.type === 'client' && onClientSelect) {
                  const resolvedId = record.userid || record.location_id;
                  if (resolvedId) {
                  return (
                    <Tooltip title={record.name}>
                      <Typography.Link
                        onClick={() => onClientSelect({ ...record, userid: String(resolvedId) })}
                        style={{ color: 'rgb(0,145,5)' }}
                        className="block truncate max-w-[200px]"
                      >
                        {record.name}
                      </Typography.Link>
                    </Tooltip>
                  );
                  }
                }
                return record.userid ? (
                  <Tooltip title={record.name}>
                    <Typography.Link
                      onClick={(event) => handleEntityLinkClick(event, record.userid!)}
                      style={{ color: record.type === 'client' ? 'rgb(0,145,5)' : '#1a73e8' }}
                      className="block truncate max-w-[200px]"
                    >
                      {record.name}
                    </Typography.Link>
                  </Tooltip>
                ) : (
                  <Tooltip title={record.name}>
                    <span className="block truncate max-w-[200px]">
                      {record.name}
                    </span>
                  </Tooltip>
                );
              case 'address':
                return value ? (
                  <Tooltip title={String(value)}>
                    <span className="block truncate max-w-[200px]">
                      {toDisplayValue(value)}
                    </span>
                  </Tooltip>
                ) : '';
              case 'orders':
              case 'products':
                if (record.type === 'product') {
                  const numeric = Number(value ?? 0);
                  if (!Number.isFinite(numeric)) {
                    return value ?? '';
                  }
                  const compact = formatNumber(numeric);
                  const exact = formatExactNumber(numeric);
                  return compact === exact ? compact : <Tooltip title={exact}>{compact}</Tooltip>;
                }
                if (record.userid) {
                  return (
                    <Typography.Link onClick={(event) => handleEntityLinkClick(event, record.userid!)}>
                      {toDisplayValue(value)}
                    </Typography.Link>
                  );
                }
                return toDisplayValue(value);
              case 'target':
              case 'total_amount':
                return renderAmount(value as string | number);
              case 'target_achievement':
              case 'revenue_share': {
                if (col.key === 'revenue_share') {
                  const share = getRevenueShare(record);
                  if (share <= 0) {
                    return '';
                  }
                  const display = `${share.toFixed(2)}%`;
                  return (
                    <Tooltip title={display}>
                      <span>{display}</span>
                    </Tooltip>
                  );
                }
                return renderPercent(value as string | number);
              }
              case 'email': {
                const raw = typeof value === 'string' ? value : '';
                if (!raw) return '';
                const masked = maskEmail(raw);
                return masked === raw ? (
                  raw
                ) : (
                  <Tooltip title={raw}>
                    <span>{masked}</span>
                  </Tooltip>
                );
              }
              case 'phone': {
                const raw = typeof value === 'string' ? value : '';
                if (!raw) return '';
                const masked = maskPhone(raw);
                return masked === raw ? (
                  raw
                ) : (
                  <Tooltip title={raw}>
                    <span>{masked}</span>
                  </Tooltip>
                );
              }
              case 'action':
                if (record.type === 'warehouse') {
                  return null;
                }
                return (
                  <Button size="small" type="primary" onClick={() => onEditLocation(record)}>
                    Edit
                  </Button>
                );
              default:
                return toDisplayValue(value);
            }
          },
        };

        return column;
      });
  }, [availableColumns, visibility, sortState, totalRevenueAmount, onEditLocation, columnOrder]);

  const tabItems = [
    { key: 'client', label: 'Clients' },
    { key: 'prospect', label: 'Prospects' },
    { key: 'vendor', label: 'Vendors' },
    { key: 'warehouse', label: 'Warehouses' },
  ];
  const visibleTabItems = allowedTabs
    ? tabItems.filter((tab) => allowedTabs.includes(tab.key as PopupTableRow['type']))
    : tabItems;
  const shouldShowTabs = visibleTabItems.length > 1;

  const handleTabChange = (value: string) => {
    const nextTab = (value as PopupTableRow['type']) ?? 'client';
    setInternalActiveTab(nextTab);
    onTabChange?.(nextTab);
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        {shouldShowTabs && (
          <Tabs
            activeKey={resolvedActiveTab}
            onChange={handleTabChange}
            items={visibleTabItems}
            size="middle"
            className="!w-full"
            tabBarGutter={8}
          />
        )}
        <Input
            allowClear
            placeholder="Search..."
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            size="large"
            suffix={<SearchOutlined className="text-lg !text-blue-600" />}
            className="w-full sm:!w-96 !rounded"
          />
        <Table<PopupTableRow>
          size="small"
          rowKey={(row) => row.userid || row.location_id || `${row.type}-${row.name}`}
          dataSource={filteredRows}
          columns={columns}
          className='border'
          pagination={{
            ...pagination,
            total: filteredRows.length,
            className: '!px-4',
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
        />
      </div>
      <ColumnSettingsModal
        isVisible={isSettingsModalVisible}
        onCancel={() => setIsSettingsModalVisible(false)}
        columns={visibility}
        columnOrder={columnOrder}
        onToggle={handleColumnToggle}
        onReset={handleResetColumns}
        onReorder={handleReorder}
      />
    </>
  );
};

interface StandardMapViewProps {
  filters: FilterState;
  locationTypeSummary: LocationTypeSummary;
  locationTypeOrder: Exclude<LocationType, 'all'>[];
  locationTypeMeta: Record<Exclude<LocationType, 'all'>, { label: string; color: string }>;
  onToggleLocationType: (type: LocationType | 'all') => void;
}

const StandardMapView: React.FC<StandardMapViewProps> = ({
  filters,
  locationTypeSummary,
  locationTypeOrder,
  locationTypeMeta,
  onToggleLocationType,
}) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        left: 16,
        bottom: 88,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '999px',
          boxShadow: '0 12px 28px rgba(15,23,42,0.15)',
          padding: '8px 14px',
          border: '1px solid rgba(148,163,184,0.3)',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onToggleLocationType('all')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            cursor: 'pointer',
            borderRadius: '999px',
            padding: '6px 12px',
            border: filters.locationType.includes('all') ? '1px solid #0f172a' : '1px solid transparent',
            backgroundColor: filters.locationType.includes('all') ? '#0f172a' : 'transparent',
            color: filters.locationType.includes('all') ? '#ffffff' : '#0f172a',
            fontSize: 12,
            fontWeight: 600,
            transition: 'all 0.2s ease',
            boxShadow: filters.locationType.includes('all') ? '0 8px 16px rgba(15,23,42,0.25)' : 'none',
            '&:hover': {
              backgroundColor: filters.locationType.includes('all') ? '#0f172a' : 'rgba(15,23,42,0.08)',
            },
            '&:focus-visible': {
              outline: '2px solid #0f172a',
              outlineOffset: 2,
            },
          }}
        >
          <Box component="span">All</Box>
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: filters.locationType.includes('all') ? '#e2e8f0' : '#64748b',
            }}
          >
            {locationTypeSummary.total.toLocaleString()}
          </Box>
        </Box>
        {locationTypeOrder.map((type) => {
          const meta = locationTypeMeta[type];
          const isActive = filters.locationType.includes('all') || filters.locationType.includes(type);
          return (
            <Box
              key={type}
              component="button"
              type="button"
              onClick={() => onToggleLocationType(type)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                cursor: 'pointer',
                borderRadius: '999px',
                padding: '6px 12px',
                border: isActive ? '1px solid ' + meta.color : '1px solid transparent',
                backgroundColor: isActive ? meta.color : 'transparent',
                color: isActive ? '#ffffff' : '#0f172a',
                fontSize: 12,
                fontWeight: 600,
                transition: 'all 0.2s ease',
                boxShadow: isActive ? '0 8px 18px ' + meta.color + '33' : 'none',
                '&:hover': {
                  backgroundColor: isActive ? meta.color : meta.color + '16',
                },
                '&:focus-visible': {
                  outline: '2px solid ' + meta.color,
                  outlineOffset: 2,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: isActive ? '#ffffff' : meta.color,
                    boxShadow: (isActive ? '0 0 0 2px ' : '0 0 0 1px ') + meta.color + '33',
                  }}
                />
                <Box sx={{ fontSize: 12, fontWeight: 600, color: isActive ? '#ffffff' : '#0f172a' }}>
                  {meta.label}
                </Box>
              </Box>
              <Box sx={{ fontSize: 11, fontWeight: 600, color: isActive ? '#ffffff' : meta.color }}>
                {locationTypeSummary.counts[type].toLocaleString()}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export { StandardMapDrawerContent };
export default StandardMapView;
